import { BizAppConfig } from 'config/application.manifest';
import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  AttachmentItem,
  AttachmentPageResult,
  AttachmentQueryParams,
  FileUrlResult,
  UpdateAttachmentDto,
  UploadAttachmentDto,
} from './types/attachment';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/attachments';

type AttachmentBizOptions = ApiRequestOptions & {
  bizType?: string;
  bizUuid?: string;
};

function withBizParams(options?: AttachmentBizOptions): ApiRequestOptions {
  const { bizType, bizUuid, params, ...rest } = options || {};
  return {
    ...rest,
    params: {
      ...(params || {}),
      ...(bizType ? { bizType } : {}),
      ...(bizUuid ? { bizUuid } : {}),
    },
  };
}

export async function queryAttachments(params: AttachmentQueryParams, options?: ApiRequestOptions) {
  return request<AttachmentPageResult>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getAttachmentDetail(uuid: string, options?: AttachmentBizOptions) {
  return request<AttachmentItem>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    ...withBizParams(options),
  });
}

export async function uploadAttachment(payload: UploadAttachmentDto, options?: ApiRequestOptions) {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('bizType', payload.bizType);
  formData.append('bizUuid', payload.bizUuid);
  if (payload.versionNo !== undefined) {
    formData.append('versionNo', String(payload.versionNo));
  }
  if (payload.remark) {
    formData.append('remark', payload.remark);
  }
  return request<AttachmentItem>(`${BASE_PATH}/upload`, {
    method: 'POST',
    data: formData,
    requestType: 'form',
    ...(options || {}),
  });
}

export async function deleteAttachment(uuid: string, options?: AttachmentBizOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...withBizParams(options),
  });
}

export async function updateAttachment(
  uuid: string,
  payload: UpdateAttachmentDto,
  options?: AttachmentBizOptions,
) {
  return request<AttachmentItem>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...withBizParams(options),
  });
}

export async function getAttachmentDownloadUrl(uuid: string, options?: AttachmentBizOptions) {
  return request<FileUrlResult>(`${BASE_PATH}/${uuid}/download-url`, {
    method: 'GET',
    ...withBizParams(options),
  });
}

export async function getAttachmentPreviewUrl(uuid: string, options?: AttachmentBizOptions) {
  return request<FileUrlResult>(`${BASE_PATH}/${uuid}/preview-url`, {
    method: 'GET',
    ...withBizParams(options),
  });
}

export function getAttachmentContentUrl(uuid: string) {
  const baseUrl = String(BizAppConfig.appApiUrl || '').trim();
  return `${baseUrl}${BASE_PATH}/${uuid}/content`;
}
